import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const LoginForm = () => {
  const navigate = useNavigate();
  const { signIn, resendConfirmationEmail } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState('');

  const handleChange = (e) => {
    // Guard against undefined/null events
    if (!e || typeof e !== 'object') {
      console.error('Invalid event object received in handleChange');
      return;
    }

    const target = e?.target || e?.currentTarget;
    if (!target) {
      console.error('Event target is undefined');
      return;
    }
    
    const { name, value, type, checked } = target;
    
    // Validate that we have a name property
    if (!name) {
      console.error('Input element missing name attribute');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field if it exists
    if (errors?.[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    // Prevent default form submission
    if (e && typeof e?.preventDefault === 'function') {
      e?.preventDefault();
    }

    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});
    setResendStatus('');

    try {
      // Trim email and password before passing to Supabase
      await signIn(formData?.email?.trim(), formData?.password?.trim());
      
      if (formData?.rememberMe) {
        localStorage.setItem('rememberMe', 'true');
      }

      // CRITICAL FIX: Use replace navigation to prevent back button issues
      navigate('/user-dashboard', { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      
      // Get the raw error message
      let errorMessage = error?.message || 'Invalid email or password. Please try again.';
      
      // CRITICAL FIX: Remove any Google/OAuth references from email/password login errors
      // Strip out OAuth provider references that shouldn't appear in email login context
      errorMessage = // Clean up multiple spaces
      errorMessage?.replace(/google/gi, '')?.replace(/oauth/gi, '')?.replace(/provider/gi, '')?.replace(/\s{2,}/g, ' ')?.trim();
      
      // Handle specific error cases for email/password authentication
      if (errorMessage?.toLowerCase()?.includes('email not confirmed')) {
        errorMessage = 'Please verify your email address before logging in. Check your inbox for the confirmation email.';
        setResendStatus('');
      } else if (errorMessage?.toLowerCase()?.includes('invalid login credentials') || 
                 errorMessage?.toLowerCase()?.includes('invalid') ||
                 errorMessage === '') {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
      }
      
      setErrors({ 
        general: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!formData?.email?.trim()) {
      setResendStatus('Enter your email above first.');
      return;
    }
    setIsResending(true);
    setResendStatus('');
    try {
      await resendConfirmationEmail(formData?.email?.trim());
      setResendStatus('Confirmation email re-sent. Please check your inbox and spam folders.');
    } catch (error) {
      setResendStatus(error?.message || 'Unable to resend confirmation email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/reset-password');
  };

  return (
    <div className="w-full max-w-md">
      <form onSubmit={handleSubmit} className="space-y-6">
        {errors?.general && (
          <div className="p-4 bg-error/10 border border-error rounded-lg flex items-start space-x-3">
            <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
            <div className="flex-1 space-y-2">
              <p className="text-sm text-error">{errors?.general}</p>
              {errors?.general?.toLowerCase()?.includes('verify your email') && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-2 sm:space-y-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleResendConfirmation}
                    loading={isResending}
                  >
                    Resend confirmation email
                  </Button>
                  {resendStatus && (
                    <span className="text-xs text-muted-foreground">{resendStatus}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <Input
          type="email"
          name="email"
          label="Email Address"
          placeholder="your.email@example.com"
          value={formData?.email || ''}
          onChange={handleChange}
          error={errors?.email}
          required
        />

        <div className="relative">
          <Input
            type={showPassword ? 'text' : 'password'}
            name="password"
            label="Password"
            placeholder="Enter your password"
            value={formData?.password || ''}
            onChange={handleChange}
            error={errors?.password}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors duration-200"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>

        <div className="flex items-center justify-between">
          <Checkbox
            name="rememberMe"
            label="Remember me"
            checked={formData?.rememberMe || false}
            onChange={handleChange}
          />

          <button
            type="button"
            onClick={handleForgotPassword}
            className="text-sm font-medium text-primary hover:text-primary/80 transition-colors duration-200"
          >
            Forgot password?
          </button>
        </div>

        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isLoading}
          iconName="LogIn"
          iconPosition="right"
        >
          {isLoading ? 'Signing in...' : 'Sign In'}
        </Button>

        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            Don't have an account?{' '}
            <button
              type="button"
              onClick={() => navigate('/user-registration')}
              className="font-medium text-primary hover:text-primary/80 transition-colors duration-200"
            >
              Create one now
            </button>
          </p>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
